/**
 * Withings Smart Sync Service
 * Handles intelligent data synchronization with delta updates
 * Start date: 01.01.2025
 */

import type { SyncResult } from "../types";
import type { WithingsMeasurementInsert } from "../types/database";
import type { WithingsScaleData } from "../types/domain";

import { core } from "@tauri-apps/api";

import { debugHealth } from "@/domains/logging/utils/debugLog";

import { WithingsTokenService } from "./withings-token-service";
import { WithingsService } from "./withingsService";

interface SyncOptions {
  endDate?: Date;
  forceFullSync?: boolean;
  startDate?: Date;
}

interface WithingsSyncToSupabaseResult {
  errors?: string[];
  new_measurements?: number;
  newMeasurements?: number;
  updated_measurements?: number;
  updatedMeasurements?: number;
}

/**
 * Smart Sync Service for Withings data
 * - First sync: Fetches all data from 01.01.2025
 * - Subsequent syncs: Only fetches missing/updated data
 *
 * NOTE: Class structure is used for namespace organization and static method grouping
 */

/**
 *
 */
export class WithingsSmartSyncService {
  private static readonly START_DATE = new Date("2025-01-01");
  // private static readonly SYNC_STORAGE_KEY = "withings_last_sync_date"; // NOTE: Unused - removed

  /**
   * Smart sync with delta updates
   *
   * @param options
   *
   * @returns {Promise<SyncResult>} Sync result with statistics
   *
   */
  static async smartSync(options: SyncOptions = {}): Promise<SyncResult> {
    try {
      // Check authentication
      const isAuthenticated = await WithingsTokenService.isAuthenticated();
      if (!isAuthenticated) {
        throw new Error("Not authenticated with Withings. Please connect your account first.");
      }

      // Get last sync date
      const lastSyncDate = options.forceFullSync ? null : await this.getLastSyncDate();

      // Determine sync date range
      const startDate = options.startDate ?? this.START_DATE;
      const endDate = options.endDate ?? new Date();

      let syncStartDate = startDate;

      // If we have a last sync date and not forcing full sync, use it
      if (lastSyncDate && !options.forceFullSync) {
        const lastSync = new Date(lastSyncDate);
        syncStartDate = new Date(lastSync.getTime() + 24 * 60 * 60 * 1000); // Next day

        // Ensure syncStartDate is not after endDate
        if (syncStartDate > endDate) {
          return {
            errors: [],
            message: "No new data to sync",
            newMeasurements: 0,
            success: true,
            totalMeasurements: 0,
            updatedMeasurements: 0,
          };
        }
      }

      // Fetch measurements from Withings API
      // Reason: Service method is properly typed, but TypeScript can't infer the return type

      const measurements = await (
        WithingsService as {
          getMeasurements: (start: Date, end: Date) => Promise<WithingsScaleData[]>;
        }
      ).getMeasurements(syncStartDate, endDate);

      if (measurements.length === 0) {
        return {
          errors: [],
          message: "No new measurements found",
          newMeasurements: 0,
          success: true,
          totalMeasurements: 0,
          updatedMeasurements: 0,
        };
      }

      // Transform measurements for Supabase
      const transformedMeasurements = this.transformMeasurementsForSupabase(measurements);

      // Sync to Supabase via Rust backend
      const syncResult = await core.invoke<WithingsSyncToSupabaseResult>(
        "withings_sync_to_supabase",
        {
          request: {
            measurements: transformedMeasurements,
          },
        },
      );

      // Update last sync date
      await this.updateLastSyncDate(endDate.toISOString());

      return {
        errors: syncResult.errors ?? [],
        // lastSyncDate: endDate.toISOString(), // NOTE: lastSyncDate not in SyncResult interface
        message: `Sync completed: ${String(
          syncResult.new_measurements ?? syncResult.newMeasurements ?? 0,
        )} new, ${String(
          syncResult.updated_measurements ?? syncResult.updatedMeasurements ?? 0,
        )} updated measurements`,
        newMeasurements: syncResult.new_measurements ?? syncResult.newMeasurements ?? 0,
        success: true,
        totalMeasurements: measurements.length,
        updatedMeasurements: syncResult.updated_measurements ?? syncResult.updatedMeasurements ?? 0,
      };
    } catch (error: unknown) {
      return {
        errors: [error instanceof Error ? error.message : "Unknown error"],
        message: "Sync failed",
        newMeasurements: 0,
        success: false,
        totalMeasurements: 0,
        updatedMeasurements: 0,
      };
    }
  }

  /**
   * Group measurements by date
   *
   * @param measurements - Array of measurements to group
   *
   * @returns {Map<string, WithingsScaleData[]>} Map of date keys to measurements
   *
   */
  private static groupMeasurementsByDate(
    measurements: WithingsScaleData[],
  ): Map<string, WithingsScaleData[]> {
    const dailyMeasurements = new Map<string, WithingsScaleData[]>();

    for (const measurement of measurements) {
      const dateKey = measurement.timestamp.toISOString().split("T")[0] ?? "";
      if (dateKey) {
        const dayMeasurements = dailyMeasurements.get(dateKey) ?? [];
        dayMeasurements.push(measurement);
        dailyMeasurements.set(dateKey, dayMeasurements);
      }
    }

    return dailyMeasurements;
  }

  /**
   * Find the latest measurement from an array
   *
   * @param measurements - Array of measurements
   *
   * @returns {WithingsScaleData | null} Latest measurement or null if empty
   *
   */
  private static findLatestMeasurement(
    measurements: WithingsScaleData[],
  ): null | WithingsScaleData {
    if (measurements.length === 0) {
      return null;
    }

    let latest = measurements[0];
    if (!latest) {
      return null;
    }

    for (let i = 1; i < measurements.length; i++) {
      const current = measurements[i];
      if (current && current.timestamp > latest.timestamp) {
        latest = current;
      }
    }

    return latest;
  }

  /**
   * Transform daily measurements to Supabase format
   *
   * @param dateKey - Date key for the measurements
   *
   * @param dayMeasurements - Measurements for the day
   *
   * @returns {WithingsMeasurementInsert | null} Transformed measurement or null if invalid
   *
   */
  private static transformDailyMeasurements(
    dateKey: string,
    dayMeasurements: WithingsScaleData[],
  ): null | WithingsMeasurementInsert {
    if (dayMeasurements.length === 0) {
      return null;
    }

    const latestMeasurement = this.findLatestMeasurement(dayMeasurements);
    if (!latestMeasurement) {
      return null;
    }

    const aggregated = this.aggregateDailyMeasurements(dayMeasurements);

    return {
      basal_metabolic_rate: aggregated.basalMetabolicRate ?? null,
      body_fat_percent: aggregated.bodyFat ?? null,
      body_water_kg: aggregated.bodyWater ?? null,
      bone_mass_kg: aggregated.boneMass ?? null,
      device_id: latestMeasurement.deviceId ?? null,
      fat_free_mass_kg: aggregated.fatFreeMass ?? null,
      fat_mass_kg: aggregated.fatMass ?? null,
      measurement_date: dateKey,
      measurement_timestamp: latestMeasurement.timestamp.toISOString(),
      muscle_mass_kg: aggregated.muscleMass ?? null,
      raw_data: {
        measurements: dayMeasurements.map((m) => ({
          basalMetabolicRate: m.basalMetabolicRate,
          bodyFat: m.bodyFat,
          bodyWater: m.bodyWater,
          boneMass: m.boneMass,
          deviceId: m.deviceId,
          fatFreeMass: m.fatFreeMass,
          fatMass: m.fatMass,
          muscleMass: m.muscleMass,
          timestamp: m.timestamp.toISOString(),
          visceralFat: m.visceralFat,
          weight: m.weight,
        })),
        total_measurements: dayMeasurements.length,
      },
      visceral_fat: aggregated.visceralFat ?? null,
      weight_kg: aggregated.weight,
      withings_user_id: latestMeasurement.userId,
    };
  }

  /**
   * Transform Withings measurements to daily Supabase format
   *
   * @param measurements
   *
   * @returns {WithingsMeasurementInsert[]} Transformed measurements for Supabase
   *
   */
  private static transformMeasurementsForSupabase(
    measurements: WithingsScaleData[],
  ): WithingsMeasurementInsert[] {
    const dailyMeasurements = this.groupMeasurementsByDate(measurements);
    const transformed: WithingsMeasurementInsert[] = [];

    for (const [dateKey, dayMeasurements] of dailyMeasurements.entries()) {
      const transformedMeasurement = this.transformDailyMeasurements(dateKey, dayMeasurements);
      if (transformedMeasurement) {
        transformed.push(transformedMeasurement);
      }
    }

    return transformed;
  }

  /**
   * Aggregate multiple measurements from the same day
   *
   * @param measurements
   *
   * @returns {WithingsScaleData} Aggregated measurement data
   *
   */
  private static aggregateDailyMeasurements(measurements: WithingsScaleData[]): WithingsScaleData {
    // Use the latest measurement for each metric (don't filter by weight)
    if (measurements.length === 0) {
      throw new Error("Cannot aggregate empty measurements array");
    }
    let latest = measurements[0];
    if (!latest) {
      throw new Error("First measurement is undefined");
    }
    for (let i = 1; i < measurements.length; i++) {
      const current = measurements[i];
      if (current && current.timestamp > latest.timestamp) {
        latest = current;
      }
    }

    const result: WithingsScaleData = {
      id: latest.id,
      timestamp: latest.timestamp,
      userId: latest.userId,
      weight: latest.weight,
    };

    if (latest.basalMetabolicRate !== undefined) {
      result.basalMetabolicRate = latest.basalMetabolicRate;
    }
    if (latest.bodyFat !== undefined) {
      result.bodyFat = latest.bodyFat;
    }
    if (latest.bodyWater !== undefined) {
      result.bodyWater = latest.bodyWater;
    }
    if (latest.boneMass !== undefined) {
      result.boneMass = latest.boneMass;
    }
    if (latest.deviceId !== undefined) {
      result.deviceId = latest.deviceId;
    }
    if (latest.fatFreeMass !== undefined) {
      result.fatFreeMass = latest.fatFreeMass;
    }
    if (latest.fatMass !== undefined) {
      result.fatMass = latest.fatMass;
    }
    if (latest.muscleMass !== undefined) {
      result.muscleMass = latest.muscleMass;
    }
    if (latest.visceralFat !== undefined) {
      result.visceralFat = latest.visceralFat;
    }

    return result;
  }

  /**
   * Get last sync date from storage
   *
   * @returns {Promise<null | string>} Last sync date or null
   *
   */
  private static async getLastSyncDate(): Promise<null | string> {
    try {
      const lastSync = await core.invoke<null | string>("get_withings_last_sync_date");
      return lastSync ?? null;
    } catch (error: unknown) {
      debugHealth("error", "withings", "[WithingsSmartSync] Error loading last sync date", {
        error: error instanceof Error ? error.message : String(error),
        file: "withingsSmartSync.ts",
        function: "getLastSyncDate",
      });
      return null;
    }
  }

  /**
   * Update last sync date in storage
   *
   * @param date
   *
   */
  private static async updateLastSyncDate(date: string): Promise<void> {
    try {
      await core.invoke("set_withings_last_sync_date", {
        request: { date },
      });
    } catch (error: unknown) {
      // NOTE: Error updating last sync date - log but don't throw
      debugHealth("error", "withings", "[WithingsSmartSync] Error updating last sync date", {
        error: error instanceof Error ? error.message : String(error),
        file: "withingsSmartSync.ts",
        function: "updateLastSyncDate",
      });
    }
  }

  /**
   * Get sync status and statistics
   *
   * @returns {Promise<{lastSyncDate: null | string; totalMeasurements: number; daysSinceLastSync: null | number}>} Sync status information
   *
   */
  static async getSyncStatus(): Promise<{
    lastSyncDate: null | string;
    totalMeasurements: number;
    daysSinceLastSync: null | number;
  }> {
    try {
      // Hole das tatsächliche letzte Messdatum aus der Datenbank
      const lastMeasurementDateRaw = await core.invoke<unknown>(
        "get_withings_last_measurement_date",
      );
      const lastMeasurementDate =
        typeof lastMeasurementDateRaw === "string" ? lastMeasurementDateRaw : null;

      const totalMeasurementsRaw = await core.invoke<unknown>("get_withings_measurement_count");
      const totalMeasurements =
        typeof totalMeasurementsRaw === "number" ? totalMeasurementsRaw : null;

      let daysSinceLastSync = null;
      if (lastMeasurementDate) {
        const lastMeasurement = new Date(lastMeasurementDate);
        const now = new Date();
        daysSinceLastSync = Math.floor(
          (now.getTime() - lastMeasurement.getTime()) / (1000 * 60 * 60 * 24),
        );
      }

      return {
        daysSinceLastSync,
        lastSyncDate: lastMeasurementDate, // Verwende das tatsächliche Messdatum
        totalMeasurements: totalMeasurements ?? 0,
      };
    } catch (error: unknown) {
      debugHealth("error", "withings", "[WithingsSmartSync] Error loading sync status", {
        error: error instanceof Error ? error.message : String(error),
        file: "withingsSmartSync.ts",
        function: "getSyncStatus",
      });
      return {
        daysSinceLastSync: null,
        lastSyncDate: null,
        totalMeasurements: 0,
      };
    }
  }

  /**
   * Force full sync from start date
   *
   * @returns {Promise<SyncResult>} Sync result
   *
   */
  static async forceFullSync(): Promise<SyncResult> {
    return this.smartSync({ forceFullSync: true });
  }

  /**
   * Sync specific date range
   *
   * @param startDate
   *
   * @param endDate
   *
   * @returns {Promise<SyncResult>} Sync result
   *
   */
  static async syncDateRange(startDate: Date, endDate: Date): Promise<SyncResult> {
    try {
      // Check authentication
      const isAuthenticated = await WithingsTokenService.isAuthenticated();
      if (!isAuthenticated) {
        throw new Error("Not authenticated with Withings. Please connect your account first.");
      }

      // Fetch measurements from Withings API for the specified date range
      // Reason: Service method is properly typed, but TypeScript can't infer the return type

      const measurements = await (
        WithingsService as {
          getMeasurements: (start: Date, end: Date) => Promise<WithingsScaleData[]>;
        }
      ).getMeasurements(startDate, endDate);

      if (measurements.length === 0) {
        return {
          errors: [],
          message: "No measurements found in the specified date range",
          newMeasurements: 0,
          success: true,
          totalMeasurements: 0,
          updatedMeasurements: 0,
        };
      }

      // Transform and sync to Supabase
      const transformedMeasurements = this.transformMeasurementsForSupabase(measurements);

      // Reason: Tauri invoke returns unknown type, needs explicit typing

      const syncResult = await core.invoke<WithingsSyncToSupabaseResult>(
        "withings_sync_to_supabase",
        {
          request: {
            measurements: transformedMeasurements,
          },
        },
      );

      // Update last sync date to the end date
      await this.updateLastSyncDate(endDate.toISOString());

      const typedSyncResult = syncResult;

      return {
        errors: typedSyncResult.errors ?? [],
        // lastSyncDate: endDate.toISOString(), // NOTE: lastSyncDate not in SyncResult interface
        message: `Successfully synced ${String(measurements.length)} measurements from ${String(startDate.toISOString().split("T")[0])} to ${String(endDate.toISOString().split("T")[0])}`,
        newMeasurements: typedSyncResult.newMeasurements ?? typedSyncResult.new_measurements ?? 0,
        success: true,
        totalMeasurements: measurements.length,
        updatedMeasurements:
          typedSyncResult.updatedMeasurements ?? typedSyncResult.updated_measurements ?? 0,
      };
    } catch (error: unknown) {
      return {
        errors: [error instanceof Error ? error.message : String(error)],
        message: "Date range sync failed",
        newMeasurements: 0,
        success: false,
        totalMeasurements: 0,
        updatedMeasurements: 0,
      };
    }
  }
}
